/*
 * -----------------------------------------------------------------------
 *  <copyright file="add-library-risk.tsx" company="Triumph Research Intelligence">
 *      Copyright (C) 2023 by Triumph Research Intelligence. All rights reserved.
 *  </copyright>
 * -----------------------------------------------------------------------
 */

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useMutation } from '@apollo/client/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FormControl, FormHelperText } from '@mui/material';
import { Checkbox } from '@progress/kendo-react-inputs';
import { Country, getData } from 'country-list';
import WarningForDialog from '@components/admin/warning-for-dialog/warning-for-dialog';
import { mapCountries } from '@pages-clean/risk-assessment/helpers';
import { areArraysIdentical, doArraysHaveTheSameMembers } from '@utils-clean/array-helpers';
import { useBoolean, useTranslation } from 'hooks';
import { LibraryRiskFieldsExtended } from 'interfaces/library-risk.interface';
import { TimeFrequencyType } from 'types/graphql-global-types';
import Constants from 'utils/constants';
import { generateReviewFrequencyUnitOptions } from 'utils/generator';
import { RiskService } from 'utils/risk.service';
import { validateFields, checkFrequencyFields } from 'utils/validation';
import { SaveButton } from 'views/components/core/buttons';
import CharactersRemaining from 'views/components/core/characters-remaining';
import { CustomDialog, DialogTitle, DialogContent, DialogActions } from 'views/components/core/dialog';
import DialogWarning from 'views/components/core/dialog-information-box/dialog-information-box';
import ModalUpdatedInfo from 'views/components/core/modal-updated-info';
import MultiSelectLov from 'views/components/core/multi-select/multi-select-lov';
import SelectWithCategories from 'views/components/core/select-with-categories';
import SelectWithCategoriesLov from 'views/components/core/select-with-categories/select-with-categories-lov';
import TextField from 'views/components/core/text-field';
import Error from 'views/components/error/error';
import { notEmpty } from '@utils/utility-helpers';
import {
    getLibraryRisks_OpraQueries_libraryRisks_LibraryRisk,
    getCompleteRisk_OpraQueries_risk_Risk_controlStrategy_NamedItem,
    getCompleteRisk_OpraQueries_risk_Risk_countries_CountryType,
    getCompleteRisk_OpraQueries_risk_Risk_responsibleFunction_NamedItem,
    getLibraryRisks_OpraQueries_libraryRisks_LibraryRisk_riskCategory_NamedItem,
    getLibraryRisks_OpraQueries_libraryRisks_LibraryRisk_vendors_NamedItem,
    updateLibraryRisk_OpraMutations_updateLibraryRisk_LibraryRisk,
    getCompleteRisk_OpraQueries_risk_Risk_category_NamedItem,
    getCompleteRisk_OpraQueries_risk_Risk_vendors_NamedItem,
} from '@queries/generated/legacy-risk-assessment.generated';
import { UPDATE_LIBRARY_RISK, GET_LIBRARY_RISKS } from '@queries/legacy-risk-assessment';
import './index.scss';

interface AddLibraryRiskErrors {
    reviewFrequencyUnits: string;
    reviewFrequencyNumber: string;
}

interface AddLibraryRiskProps {
    onClose: (action: string | boolean | undefined, isRisk?: boolean) => void;
    open: boolean;
    risk: LibraryRiskFieldsExtended;
    canEdit: boolean;
    riskLibraryCategories: getCompleteRisk_OpraQueries_risk_Risk_category_NamedItem[];
    riskCategories: getLibraryRisks_OpraQueries_libraryRisks_LibraryRisk_riskCategory_NamedItem[];
    organisationId: string;
    currentControl: getLibraryRisks_OpraQueries_libraryRisks_LibraryRisk;
    onAssign: (risk: updateLibraryRisk_OpraMutations_updateLibraryRisk_LibraryRisk) => void;
    responsibleFunctions: getCompleteRisk_OpraQueries_risk_Risk_responsibleFunction_NamedItem[];
    isSystemLevel: boolean;
    controlStrategies: any;
    vendors: getCompleteRisk_OpraQueries_risk_Risk_vendors_NamedItem[];
}

const AddLibraryRisk = ({
    onClose,
    open,
    risk,
    canEdit,
    riskLibraryCategories,
    riskCategories,
    organisationId,
    currentControl,
    onAssign,
    responsibleFunctions,
    isSystemLevel,
    controlStrategies,
    vendors,
}: AddLibraryRiskProps) => {
    const { t } = useTranslation();
    const [saveLibraryRisk, { error }] = useMutation(UPDATE_LIBRARY_RISK);
    const [statement, setStatement] = useState<string | null>('');
    const [filteredRiskLibraryCategories, setFilteredRiskLibraryCategories] = useState<
        getCompleteRisk_OpraQueries_risk_Risk_category_NamedItem[]
    >([]);
    const [filteredResponsibleFunctions, setFilteredResponsibleFunctions] = useState<
        getCompleteRisk_OpraQueries_risk_Risk_responsibleFunction_NamedItem[]
    >([]);
    const [statementError, setStatementError] = useState<string | null>(null);
    const [riskCategory, setRiskCategory] =
        useState<getLibraryRisks_OpraQueries_libraryRisks_LibraryRisk_riskCategory_NamedItem | null>(null);

    const [filteredVendors, setFilteredVendors] = useState<getCompleteRisk_OpraQueries_risk_Risk_vendors_NamedItem[]>(
        [],
    );
    const [filteredCountries, setFilteredCountries] = useState<Array<Country>>([]);
    const [controlStrategy, setControlStrategy] =
        useState<getCompleteRisk_OpraQueries_risk_Risk_controlStrategy_NamedItem | null>(null);

    const [reviewFrequencyUnits, setReviewFrequencyUnits] = useState<string | null>('');
    const [reviewFrequencyNumber, setReviewFrequencyNumber] = useState<number | null>(0);
    const [responsibleFunction, setResponsibleFunction] =
        useState<getCompleteRisk_OpraQueries_risk_Risk_responsibleFunction_NamedItem | null>(null);
    const active = useBoolean(true);
    const [errors, setErrors] = useState({} as AddLibraryRiskErrors);
    const reviewRequired = useBoolean(false);
    const initialValuesRef = useRef<any>(null);

    const reviewFrequencyTypeOptions = Object.assign([], RiskService.timeFrequencyListObject);
    reviewFrequencyTypeOptions.unshift({
        id: 'NONE',
        text: t('ra:selectReviewFrequency'),
    });

    const { reviewFrequencyNumberMin } = RiskService;
    const { reviewFrequencyNumberMax } = RiskService;

    const countries = Object.assign(
        [],
        getData().map((countryObject: { code: string; name: string }) => ({
            id: countryObject.code,
            code: countryObject.code,
            name: countryObject.name,
            active: true,
        })),
    );

    useEffect(() => {
        if (open) {
            if (risk) {
                active.setValue(!!risk.active);
                setStatement(risk.statement);
                setFilteredRiskLibraryCategories(
                    riskLibraryCategories.filter((item) => risk.riskLibraryCategoryIds.indexOf(item.id) >= 0),
                );
                setRiskCategory(risk.riskCategory);
                setControlStrategy(risk.controlStrategy);
                setReviewFrequencyUnits(risk.reviewFrequencyUnits);
                setReviewFrequencyNumber(risk?.reviewFrequencyPeriod);
                setResponsibleFunction(risk?.responsibleFunction);
                reviewRequired.setValue(!!risk.reviewRequired);
                setFilteredVendors(vendors.filter((item) => risk.vendorIds.indexOf(item.id) >= 0));
                setFilteredCountries(
                    risk.countries
                        ?.filter(notEmpty)
                        .map(({ code, name }) => ({ code: code ?? '', name: name ?? '' })) ?? [],
                );
                setErrors({} as AddLibraryRiskErrors);

                initialValuesRef.current = {
                    active: !!risk.active,
                    categories: riskLibraryCategories
                        .filter((item) => risk.riskLibraryCategoryIds.indexOf(item.id) >= 0)
                        .map((item: getCompleteRisk_OpraQueries_risk_Risk_category_NamedItem) => item.id),
                    riskCategory: risk.riskCategory?.id,
                    controlStrategy: risk.controlStrategy?.id,
                    responsibleFunction: risk?.responsibleFunction?.id,
                    reviewRequired: !!risk.reviewRequired,
                    reviewFrequencyUnits: risk.reviewFrequencyUnits,
                    reviewFrequencyNumber: risk?.reviewFrequencyPeriod,
                    vendors: vendors.filter((item) => risk?.vendorIds.indexOf(item.id) >= 0).map((item) => item.id),
                    countries: risk.countries?.filter(notEmpty).map((item: Country) => item.code),
                    statement: risk.statement,
                };
            } else {
                active.setTrue();
                setStatement('');
                setFilteredRiskLibraryCategories([]);
                setRiskCategory(null);
                setControlStrategy(null);
                setReviewFrequencyUnits(TimeFrequencyType.NONE);
                setReviewFrequencyNumber(0);
                setResponsibleFunction(null);
                setFilteredVendors([]);
                setFilteredCountries([]);
                reviewRequired.setValue(true);
            }
            setFilteredResponsibleFunctions(
                responsibleFunctions.filter(
                    (item) => item.organisationId === organisationId && item.studyId === Constants.ObjectId.Empty,
                ),
            );
        }
    }, [risk, open]);

    const isDirty = useMemo(() => {
        if (!initialValuesRef.current) {
            if (isSystemLevel) {
                return statement && statement?.length > 0 && validateFields(statement, 'statement', 600, false);
            }
            return (
                statement &&
                statement?.length > 0 &&
                validateFields(statement, 'statement', 600, false) &&
                (!reviewRequired.value ||
                    (reviewFrequencyUnits !== TimeFrequencyType.NONE && reviewFrequencyNumber !== 0))
            );
        }

        if (!risk || !open) return false;

        return (
            active.value !== initialValuesRef.current.active ||
            statement !== initialValuesRef.current.statement ||
            reviewFrequencyUnits !== initialValuesRef.current.reviewFrequencyUnits ||
            reviewFrequencyNumber !== initialValuesRef.current.reviewFrequencyNumber ||
            riskCategory?.id !== initialValuesRef.current.riskCategory ||
            controlStrategy?.id !== initialValuesRef.current.controlStrategy ||
            responsibleFunction?.id !== initialValuesRef.current.responsibleFunction ||
            !doArraysHaveTheSameMembers(
                filteredRiskLibraryCategories.map((item) => item.id),
                initialValuesRef.current.categories,
            ) ||
            !areArraysIdentical(
                filteredVendors.map((item) => item.id),
                initialValuesRef.current.vendors,
            ) ||
            !areArraysIdentical(
                filteredCountries.map((item) => item.code),
                initialValuesRef.current.countries,
            ) ||
            (reviewRequired.value !== initialValuesRef.current.reviewRequired &&
                reviewRequired.value &&
                reviewFrequencyUnits !== TimeFrequencyType.NONE &&
                reviewFrequencyNumber !== 0)
        );
    }, [
        active.value,
        statement,
        reviewRequired.value,
        reviewFrequencyUnits,
        reviewFrequencyNumber,
        filteredRiskLibraryCategories,
        filteredVendors,
        filteredCountries,
        riskCategory,
        controlStrategy,
        responsibleFunction,
    ]);

    const getReviewFrequencyUnitOptions = () =>
        generateReviewFrequencyUnitOptions(reviewFrequencyNumberMin, reviewFrequencyNumberMax);

    const close = (action: string | boolean | undefined) => {
        setStatementError(null);
        initialValuesRef.current = null;
        onClose(action, true);
    };

    const validate = (value: string) => {
        const invalidStatement = validateFields(value, 'statement', 600, false);
        if (invalidStatement) {
            setStatementError(invalidStatement.statement);
        }
    };

    const getRefetchQueries = () => {
        if (currentControl) return [];
        return [
            {
                query: GET_LIBRARY_RISKS,
                variables: {
                    organisationId,
                    includeSystemRisks: isSystemLevel,
                },
            },
        ];
    };

    const saveRisk = (data?: any) => {
        saveLibraryRisk({
            variables: {
                risk: {
                    id: risk?.id || null,
                    organisationId,
                    statement,
                    active: active.value,
                    riskLibraryCategoryIds: filteredRiskLibraryCategories.map(
                        (item: getLibraryRisks_OpraQueries_libraryRisks_LibraryRisk_riskCategory_NamedItem) => item.id,
                    ),
                    riskCategoryId: riskCategory?.id,
                    libraryControlIds: risk ? risk.libraryControlIds : [],
                    systemReferenceId: data?.id || Constants.ObjectId.Empty,
                    controlStrategyId: controlStrategy?.name === 'NONE' ? null : controlStrategy?.id,
                    reviewFrequencyPeriod: reviewFrequencyNumber,
                    reviewFrequencyUnits,
                    responsibleFunctionId: responsibleFunction?.id,
                    displayOrder: risk?.displayOrder,
                    reviewRequired: reviewRequired.value,
                    vendorIds: filteredVendors.map(
                        (item: getLibraryRisks_OpraQueries_libraryRisks_LibraryRisk_vendors_NamedItem) => item.id,
                    ),
                    countries: filteredCountries?.map(({ code, name }) => ({ code, name })) ?? [],
                },
            },
            refetchQueries: getRefetchQueries(),
        })
            .then((result: any) => {
                initialValuesRef.current = null;
                if (currentControl) onAssign(result.data.updateLibraryRisk);
                else close(risk ? 'edit' : 'add');
            })
            .catch(() => {
                close('error');
            });
    };

    if (error) return <Error error={error} />;

    const getButtonLabel = () => {
        if (currentControl && !risk) return 'createAssign';
        return !risk ? 'common:add' : 'common:update';
    };

    const isCategoryInRiskLibrary = (id: string) => risk.riskLibraryCategoryIds.indexOf(id) >= 0;

    const getItems = () => {
        // When filtering items for risk, we need to take into account that
        // filteredRiskLibraryCategories in current risk may not be active and add them to items
        // to allow user to restore them if accidentally deleted
        if (risk)
            return (
                riskLibraryCategories?.filter(
                    (item: any) =>
                        !filteredRiskLibraryCategories?.find(
                            (el: getLibraryRisks_OpraQueries_libraryRisks_LibraryRisk_riskCategory_NamedItem) =>
                                el?.id === item.id,
                        ) &&
                        (item.active || isCategoryInRiskLibrary(item.id)),
                ) || riskLibraryCategories
            );

        return (
            riskLibraryCategories?.filter(
                (item: any) =>
                    !filteredRiskLibraryCategories
                        ?.filter(
                            (element: getLibraryRisks_OpraQueries_libraryRisks_LibraryRisk_riskCategory_NamedItem) =>
                                element,
                        )
                        .find((el: any) => el?.id === item.id) && item.active,
            ) || riskLibraryCategories
        );
    };

    const isVendorInRiskVendors = (id: string) => risk.vendorIds.indexOf(id) >= 0;

    const getVendorItems = () => {
        if (risk)
            return (
                vendors?.filter(
                    (item: any) =>
                        !filteredVendors?.find(
                            (el: getLibraryRisks_OpraQueries_libraryRisks_LibraryRisk_vendors_NamedItem) =>
                                el?.id === item.id,
                        ) &&
                        (item.active || isVendorInRiskVendors(item.id)),
                ) || vendors
            );

        return (
            vendors?.filter(
                (item: any) =>
                    !filteredVendors
                        ?.filter(
                            (element: getLibraryRisks_OpraQueries_libraryRisks_LibraryRisk_vendors_NamedItem) =>
                                element,
                        )
                        .find((el: any) => el?.id === item.id) && item.active,
            ) || vendors
        );
    };

    const getCountryItems = () => {
        if (risk) {
            const countriesIncludingInvalid = mapCountries(
                countries,
                (risk.countries?.filter(notEmpty).map((country) => ({ code: country.code, name: country.name })) ??
                    []) as Country[],
            );
            return (
                countriesIncludingInvalid.filter(
                    (item: any) =>
                        !filteredCountries?.find(
                            (el: getCompleteRisk_OpraQueries_risk_Risk_countries_CountryType) => el?.code === item.code,
                        ),
                ) || countriesIncludingInvalid
            );
        }

        return (
            countries?.filter(
                (item: any) =>
                    !filteredCountries
                        ?.filter((element: getCompleteRisk_OpraQueries_risk_Risk_countries_CountryType) => element)
                        .find((el: any) => el?.code === item.code),
            ) || countries
        );
    };

    const onChangeRiskCategory = (fieldName: string, value: string) => {
        const riskCategoryValue = riskCategories.find(
            (category: getLibraryRisks_OpraQueries_libraryRisks_LibraryRisk_riskCategory_NamedItem) =>
                category.id === value,
        );
        setRiskCategory(riskCategoryValue);
    };

    const onChangeReviewFrequencyPeriod = (fieldName: string, value: string) => {
        const tempState = {
            reviewFrequencyNumber,
            reviewFrequencyUnits,
        };

        const frequencyErrors = checkFrequencyFields(fieldName, value, tempState);
        setErrors({
            ...errors,
            ...error,
            ...frequencyErrors,
        });

        setReviewFrequencyUnits(value);
    };

    const onChangereviewFrequencyNumber = (fieldName: string, value: string) => {
        const tempState = {
            reviewFrequencyNumber,
            reviewFrequencyUnits,
        };

        const frequencyErrors = checkFrequencyFields(fieldName, value, tempState);
        setErrors({
            ...errors,
            ...error,
            ...frequencyErrors,
        });
        setReviewFrequencyNumber(+value);
    };

    const onChangeResponsibleFunction = () => (event: any) => {
        const { value } = event.target;
        const responsibleFunctionValue = filteredResponsibleFunctions.find(
            (item: getCompleteRisk_OpraQueries_risk_Risk_responsibleFunction_NamedItem) => item.id === value,
        );

        setResponsibleFunction(responsibleFunctionValue);
    };

    const onChangeControlStrategy = () => (event: any) => {
        const { value } = event.target;
        const controlStrategyValue = controlStrategies.find(
            (item: getCompleteRisk_OpraQueries_risk_Risk_controlStrategy_NamedItem) => item.id === value,
        );
        setControlStrategy(controlStrategyValue);
    };

    const handleReviewRequired = (e: boolean) => {
        if (reviewRequired.value) {
            setReviewFrequencyUnits(TimeFrequencyType.NONE);
            setReviewFrequencyNumber(0);
            setErrors({
                ...errors,
                ...error,
                reviewFrequencyUnits: null,
                reviewFrequencyNumber: null,
            });
        }
        reviewRequired.setValue(!reviewRequired.value);
    };

    const validateReviewRequiredFields = () => {
        if (reviewRequired.value) {
            onChangeReviewFrequencyPeriod('reviewFrequencyUnits', (reviewFrequencyUnits ?? 'NONE').toString());
            onChangereviewFrequencyNumber('reviewFrequencyNumber', (reviewFrequencyNumber ?? 0).toString());
        }
    };

    if (!open) return null;

    return (
        <CustomDialog
            onClose={() => close(false)}
            aria-labelledby="add-risk-title"
            fullWidth
            scroll="paper"
            maxWidth="md"
            open
        >
            <DialogTitle id="add-risk-title">
                {risk ? `${t('ra:editRisk')}: ${risk.recordId || risk.id}` : t('ra:addRisk')}
                <FontAwesomeIcon
                    icon={['fal', 'times']}
                    className="fa-lg close-icon-cv"
                    onClick={() => close(false)}
                    data-testid="close-dialog"
                />
            </DialogTitle>

            <DialogContent className="add-risk-dialog-content edit-risk">
                {risk?.associatedControls?.length ? (
                    <WarningForDialog type="risk" firstNumber={risk.associatedControls.length} secondNumber={0} />
                ) : null}
                <Checkbox
                    rounded={null}
                    className="cell-checkbox no-full-width flex-start no-border"
                    id="risk-active-checkbox"
                    checked={active.value}
                    onChange={(e) => {
                        active.setValue(e.value);
                    }}
                    label={t('active')}
                    data-testid="risk-active-checkbox"
                    size="medium"
                />
                {!isSystemLevel && (
                    <>
                        <div className="type-edc-wrapper ">
                            <MultiSelectLov
                                items={getItems()}
                                onChange={(value: any) => {
                                    if (value.includes(undefined)) return;
                                    setFilteredRiskLibraryCategories([...value]);
                                }}
                                value={filteredRiskLibraryCategories}
                                label={t('admin:riskLibraryCategory')}
                                customClassName="form-action-type risk-categories"
                                customId="category-risk"
                                placeholder={t('enterSearchTerm')}
                                field="riskLibraryCategory"
                            />
                        </div>
                        <div className="type-edc-wrapper mb-30">
                            <FormControl variant="outlined" className="form-control-type not-full">
                                <SelectWithCategoriesLov
                                    label={t('admin:riskCategory')}
                                    onChange={(fieldName?: string) => (event: any) => {
                                        onChangeRiskCategory(fieldName, event.target.value);
                                    }}
                                    namedItems={riskCategories}
                                    labelField="riskCategory"
                                    fieldSelect="riskCategory"
                                    customId="add-library-risk-riskCategory"
                                    customClassName="add-library-risk-riskCategory"
                                    currentItem={riskCategory}
                                    initialState={risk?.riskCategory?.id}
                                    defaultText={t('admin:selectACategory')}
                                />
                            </FormControl>
                        </div>

                        <div className="type-edc-wrapper mb-30">
                            <FormControl variant="outlined" className="form-control-type mr">
                                <SelectWithCategoriesLov
                                    label={t('ra:controlStrategy')}
                                    onChange={onChangeControlStrategy}
                                    namedItems={controlStrategies}
                                    labelField="controlStrategy"
                                    fieldSelect="controlStrategy"
                                    customId="add-library-risk-controlStrategy"
                                    customClassName="add-library-risk-controlStrategy"
                                    currentItem={controlStrategy}
                                    initialState={risk?.controlStrategy?.id}
                                    defaultText={t('ra:selectControlStrategy')}
                                />
                            </FormControl>

                            <FormControl variant="outlined" className="form-control-type">
                                <SelectWithCategoriesLov
                                    label={t('ra:responsibleFunction')}
                                    onChange={onChangeResponsibleFunction}
                                    namedItems={filteredResponsibleFunctions}
                                    labelField="responsibleFunction"
                                    fieldSelect="responsibleFunction"
                                    customId="add-library-risk-responsibleFunction"
                                    customClassName="add-library-risk-responsibleFunction"
                                    currentItem={responsibleFunction}
                                    initialState={risk?.responsibleFunction?.id}
                                    defaultText={t('ra:selectResponsibleFunction')}
                                    grouped
                                />
                            </FormControl>
                        </div>
                        <div className="type-edc-wrapper">
                            <FormControl variant="outlined" className="form-control-type mr">
                                <Checkbox
                                    rounded={null}
                                    className="cell-checkbox no-full-width flex-start no-border"
                                    id="review-required-checkbox"
                                    checked={reviewRequired.value}
                                    onChange={(e) => {
                                        handleReviewRequired(e.value);
                                    }}
                                    label={t('admin:reviewRequired')}
                                    data-testid="review-required-checkbox"
                                    size="medium"
                                />
                            </FormControl>
                        </div>
                        <div className="type-edc-wrapper">
                            <FormControl variant="outlined" required className="form-control-type mr">
                                <SelectWithCategories
                                    label={t('admin:reviewFrequencyPeriod') + (reviewRequired.value ? '*' : '')}
                                    onChange={(fieldName?: string) => (event: any) => {
                                        onChangeReviewFrequencyPeriod(fieldName, event.target.value);
                                    }}
                                    dataArray={reviewFrequencyTypeOptions}
                                    fieldSelect="reviewFrequencyUnits"
                                    customClassName="form-action-type"
                                    showWarning={false}
                                    filterable={false}
                                    currentState={reviewFrequencyUnits}
                                    customId="edit-control-review-frequency-units"
                                    disabled={!reviewRequired.value}
                                />
                                {!!errors.reviewFrequencyUnits && (
                                    <FormHelperText className="error-text">
                                        {errors.reviewFrequencyUnits}
                                    </FormHelperText>
                                )}
                            </FormControl>

                            <FormControl variant="outlined" className="form-control-type">
                                <SelectWithCategories
                                    label={t('admin:reviewFrequencyUnit') + (reviewRequired.value ? '*' : '')}
                                    customClassName="form-action-type"
                                    fieldSelect="reviewFrequencyNumber"
                                    onChange={(fieldName?: string) => (event: any) => {
                                        onChangereviewFrequencyNumber(fieldName, event.target.value);
                                    }}
                                    dataArray={getReviewFrequencyUnitOptions()}
                                    showWarning={false}
                                    filterable={false}
                                    currentState={reviewFrequencyNumber}
                                    customId="edit-control-review-frequency-number"
                                    disabled={!reviewRequired.value}
                                />
                                {!!errors.reviewFrequencyNumber && (
                                    <FormHelperText className="error-text">
                                        {errors.reviewFrequencyNumber}
                                    </FormHelperText>
                                )}
                            </FormControl>
                        </div>
                        <div className="mb-10">
                            <MultiSelectLov
                                items={getVendorItems()}
                                onChange={(value: any) => {
                                    if (value.includes(undefined)) return;
                                    setFilteredVendors([...value]);
                                }}
                                value={filteredVendors}
                                label={t('admin:vendors')}
                                customClassName="form-action-type risk-categories"
                                customId="vendors-risk"
                                placeholder={t('enterSearchTerm')}
                                field="vendors"
                            />
                        </div>

                        <div className="mb-20">
                            <MultiSelectLov
                                items={getCountryItems()}
                                onChange={(value: any) => {
                                    if (value.includes(undefined)) return;
                                    setFilteredCountries([...value]);
                                }}
                                value={filteredCountries}
                                label={t('admin:countries')}
                                customClassName="form-action-type risk-countries"
                                customId="countries-risk"
                                placeholder={t('enterSearchTerm')}
                                field="countries"
                                isCountryMultiSelect
                            />
                        </div>
                    </>
                )}

                <DialogWarning topText={t('ra:guidelineHtml')} bottomText={t('ra:guidelineExampleText')} />
                <TextField
                    id="edit-risk-statement"
                    placeholder={t('ra:newRiskPlaceholder')}
                    className="!k-mt-5"
                    autoFocus
                    multiline
                    disabled={!canEdit}
                    rows={5}
                    fullWidth
                    label={t('ra:statement*')}
                    value={statement}
                    helperText={
                        <CharactersRemaining
                            id="edit-risk-dialog-statement"
                            length={statement.length}
                            max={600}
                            error={statementError}
                        />
                    }
                    onChange={(e: any) => {
                        setStatement(e.target.value);
                        setStatementError(null);
                        validate(e.target.value);
                    }}
                    margin="normal"
                    variant="outlined"
                    error={!!statementError}
                />
            </DialogContent>

            <DialogActions
                className={`actions-info-wrapper-edit-cv actions-add-risk assign-items ${!risk ? 'flex-end' : ''}`}
            >
                <ModalUpdatedInfo item={risk} />
                <SaveButton
                    id="edit-risk-save"
                    disabled={!isDirty || statementError}
                    text={getButtonLabel()}
                    onClick={() => {
                        const validationError = validateFields(statement, 'statement', 600, true);

                        if (!isSystemLevel && reviewRequired.value) {
                            validateReviewRequiredFields();
                        }
                        if (
                            validationError &&
                            (validationError.statement ||
                                (!isSystemLevel &&
                                    reviewRequired.value &&
                                    (reviewFrequencyNumber === null ||
                                        reviewFrequencyNumber < 1 ||
                                        reviewFrequencyUnits === null ||
                                        reviewFrequencyUnits === 'NONE')))
                        ) {
                            setStatementError(validationError.statement);
                            return;
                        }
                        saveRisk();
                    }}
                />
            </DialogActions>
        </CustomDialog>
    );
};

export default AddLibraryRisk;
